<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

interface Props {
  beforeImage: string
  afterImage: string
  width?: string
  height?: string
}

const props = withDefaults(defineProps<Props>(), {
  width: '100%',
  height: 'auto'
})

const sliderRef = ref<HTMLElement | null>(null)
const isDragging = ref(false)
const sliderPosition = ref(50)
const containerRef = ref<HTMLElement | null>(null)

function handleMouseDown(event: MouseEvent) {
  isDragging.value = true
  updateSliderPosition(event)
}

function handleMouseMove(event: MouseEvent) {
  if (!isDragging.value) return
  updateSliderPosition(event)
}

function handleMouseUp() {
  isDragging.value = false
}

function handleTouchStart(event: TouchEvent) {
  isDragging.value = true
  updateSliderPosition(event.touches[0])
}

function handleTouchMove(event: TouchEvent) {
  if (!isDragging.value) return
  updateSliderPosition(event.touches[0])
}

function handleTouchEnd() {
  isDragging.value = false
}

function updateSliderPosition(event: MouseEvent | Touch) {
  if (!containerRef.value) return

  const rect = containerRef.value.getBoundingClientRect()
  const x = event.clientX - rect.left
  const position = (x / rect.width) * 100

  sliderPosition.value = Math.min(Math.max(position, 0), 100)
}

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove)
  window.addEventListener('mouseup', handleMouseUp)
  window.addEventListener('touchmove', handleTouchMove)
  window.addEventListener('touchend', handleTouchEnd)
})

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove)
  window.removeEventListener('mouseup', handleMouseUp)
  window.removeEventListener('touchmove', handleTouchMove)
  window.removeEventListener('touchend', handleTouchEnd)
})
</script>

<template>
  <div
    ref="containerRef"
    class="image-comparison-container"
    :style="{ width, height }"
    @mousedown="handleMouseDown"
    @touchstart="handleTouchStart"
  >
    <img :src="afterImage" class="after-image" alt="After" />
    <div class="before-image-container" :style="{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }">
      <img :src="beforeImage" class="before-image" alt="Before" />
    </div>
    <div ref="sliderRef" class="slider" :style="{ left: `${sliderPosition}%` }">
      <div class="slider-line"></div>
      <div class="slider-handle">
        <q-icon name="mdi-chevron-left" />
        <q-icon name="mdi-chevron-right" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.image-comparison-container {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  cursor: ew-resize;
  background-color: #000000;
}

.after-image,
.before-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000000;
}

.before-image-container {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  overflow: hidden;
  background-color: #000000;
  clip-path: inset(0 0 0 0);
  width: 100%;
}

.before-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000000;
}

.slider {
  position: absolute;
  top: 0;
  height: 100%;
  width: 2px;
  background: white;
  transform: translateX(-50%);
  z-index: 2;
}

.slider-line {
  position: absolute;
  top: 0;
  left: 50%;
  height: 100%;
  width: 2px;
  background: white;
  transform: translateX(-50%);
}

.slider-handle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.slider-handle .q-icon {
  font-size: 20px;
  color: #666;
}
</style>
